<script setup lang="ts">
import { cn } from "@/lib/utils";
import { eventBus } from "#bb/lib/eventBus";
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

const logger = useLogger("ReportProviderMock");
const props = defineProps<{ id: string }>();
const { getContentById } = useContentManager();
const definitionData = computed(() => getContentById(props.id));
const features = inject("render-features", { editor: false });

const commands = [
  {
    id: "editors-group",
    type: "group",
    name: "Editors",
    description: "Mock Report Provider editors",
    commands: [
      {
        id: "edit",
        name: "Edit",
        description: "Edit this mock report provider",
        action: async () => {
          (eventBus as any).emit(`component:onEdit:${props.id}`, { data: definitionData.value });
        },
        icon: "lucide:edit",
      },
    ],
  },
];

defineExpose({
  commands,
  properties: {
    id: props.id,
    name: "Mock Report Provider",
    icon: "lucide:braces",
  },
});

const mockData = computed<unknown>(() => {
  const source = definitionData.value?.mockData;
  if (typeof source !== "string" || !source.trim()) {
    return [];
  }

  try {
    return JSON.parse(source);
  } catch (error) {
    logger.warn("Invalid mock report JSON", error);
    return [];
  }
});

const mockContext = computed<Record<string, unknown>>(() => {
  const data = mockData.value;
  if (typeof data === "object" && data !== null && !Array.isArray(data)) {
    return data as Record<string, unknown>;
  }

  return { data };
});

const report = {
  get data(): unknown {
    return mockData.value;
  },
  kind: "mock" as const,
  get context(): Record<string, unknown> {
    return mockContext.value;
  },
  loading: false,
  initialized: true,
  debug: false,
  fileName: "mock",
};

provide("report", report);
</script>

<template>
  <div :class="cn($attrs.class ?? '')">
    <slot />
  </div>
  <div v-if="features.editor" class="hidden">
    <editor-reports-mock-provider :id="props.id" />
  </div>
</template>

<script lang="ts">
import { ComponentDefinitionIds } from "#bb/lib/componentDefinitionIds";

export default {
  editor: () => import("#bb/components/editor/reports/mock-provider.vue"),
  componentId: ComponentDefinitionIds.REPORT_PROVIDER_MOCK,
  id: "reportprovider-mock",
  icon: "lucide:braces",
  type: "Mock Report Provider",
  description: "Provides pasted JSON to child blocks without connecting to report data.",
  default: {
    columnsSettings: { default: 1 },
    mockData: "[]",
    children: [],
  },
  categories: ["Reports"],
  order: 12006,
};
</script>
